import { useEffect, useRef, useState } from "react";
import type {
  DocumentLos,
  Lender,
  LenderProduct,
  ReportDataPoint,
  UpdateProductConfigurationRequest,
} from "../../api/lenders";
import type { Product } from "../../api/products";
import { useUpdateLenderProductConfiguration } from "../../hooks/useLenders";
import { US_STATES } from "./wizard/usStates";
import badgeDot from "./assets/product-badge-dot.svg";
import closeIcon from "./assets/product-close.svg";
import toggleOff from "./assets/product-toggle-off.svg";
import toggleOn from "./assets/product-toggle-on.svg";
import chevronDown from "./assets/product-chevron-down.svg";

const documentFields: Record<DocumentLos, { label: string; key: string }[]> = {
  encompass: [{ label: "File Name", key: "fileNameTemplate" }],
  meridianlink: [
    { label: "Title", key: "titleTemplate" },
    { label: "File Name", key: "fileNameTemplate" },
    { label: "Doc Group", key: "docGroup" },
    { label: "Doc Code", key: "docCode" },
  ],
  "meridianlink-mortgage": [
    { label: "Doc Type", key: "documentType" },
    { label: "File Name", key: "fileNameTemplate" },
    { label: "Description", key: "descriptionTemplate" },
  ],
};

const losNames: Record<DocumentLos, string> = {
  encompass: "Encompass",
  meridianlink: "MeridianLink Consumer",
  "meridianlink-mortgage": "MeridianLink Mortgage",
};

type DataPointField = { key: ReportDataPoint; label: string; paths?: string[] };
const dataPointFieldsByService: Record<string, DataPointField[]> = {
  flood: [
    {
      key: "censusTrackNumber",
      label: "Census Track Number",
      paths: ["hmda.censusTract", "hmda.censusTrack"],
    },
    {
      key: "floodZone",
      label: "Flood Zone",
      paths: ["contacts.insuranceFloodZone"],
    },
    {
      key: "certificateNumber",
      label: "Certificate Number",
      paths: [
        "contacts.insuranceCertNumber",
        "property.floodCertificationIdentifier",
      ],
    },
    {
      key: "floodZoneCode",
      label: "Flood Zone Code",
      paths: ["closingDocument.specialFloodHazardAreaIndictor"],
    },
    {
      key: "communityName",
      label: "Community Name",
      paths: ["disclosureNotices.femaCommunityName"],
    },
    { key: "msaNumber", label: "MSA Number", paths: ["hmda.msaNumber"] },
    {
      key: "communityNumber",
      label: "Community Number",
      paths: ["disclosureNotices.nfipCommunityNumber"],
    },
    {
      key: "panelNumber",
      label: "Panel Number",
      paths: ["disclosureNotices.mapPanelNumber"],
    },
    { key: "countyCode", label: "County Code", paths: ["hmda.countyCode"] },
    { key: "stateCode", label: "State Code", paths: ["hmda.stateCode"] },
    {
      key: "effectiveRevisedDate",
      label: "Effective Revised Date",
      paths: ["disclosureNotices.nfipMapEffectiveRevisedDate"],
    },
  ],
  title: [
    {
      key: "propertyTaxId",
      label: "Property Tax ID",
      paths: ["property.assessorsParcelIdentifier"],
    },
    {
      key: "legalDescription",
      label: "Legal Description",
      paths: [
        "closingDocument.shortDescription",
        "closingDocument.shortTextDescription",
        "closingDocument.textDescription",
      ],
    },
    {
      key: "vestingDescription",
      label: "Vesting Description",
      paths: ["closingDocument.finalVestingDescription"],
    },
  ],
  valuation: [
    {
      key: "estimatedValue",
      label: "Estimated Value",
      paths: ["extensions.estimatedValue"],
    },
    {
      key: "valuationDate",
      label: "Valuation Date",
      paths: ["extensions.estimationDate"],
    },
    {
      key: "confidenceScore",
      label: "Confidence Score",
      paths: ["extensions.confidenceScore"],
    },
    { key: "fsdScore", label: "FSD Score", paths: ["extensions.fsdScore"] },
    {
      key: "minimumValue",
      label: "Minimum Value",
      paths: ["extensions.minPrice"],
    },
    {
      key: "maximumValue",
      label: "Maximum Value",
      paths: ["extensions.maxPrice"],
    },
  ],
};

const hasSchemaPath = (schema: unknown, path: string): boolean =>
  path.split(".").reduce<unknown>((node, segment) => {
    if (!node || typeof node !== "object") return undefined;
    const typed = node as {
      properties?: Record<string, unknown>;
      items?: unknown;
    };
    return (
      typed.properties?.[segment] ??
      (typed.items as { properties?: Record<string, unknown> } | undefined)
        ?.properties?.[segment]
    );
  }, schema) !== undefined;
const stateOptions = US_STATES.filter(
  ({ code }) => !["DC", "AS", "GU", "MP", "PR", "VI"].includes(code),
).sort((left, right) => left.code.localeCompare(right.code));
const sameSelection = (left: readonly string[], right: readonly string[]) =>
  left.length === right.length && left.every((item) => right.includes(item));

export default function LenderProductEditDrawer({
  lender,
  product,
  catalog,
  onClose,
}: {
  lender: Lender;
  product: LenderProduct;
  catalog?: Product[];
  onClose: () => void;
}) {
  const catalogProduct = catalog?.find(
    (item) =>
      item.urn === product.urn ||
      item.vendorProducts?.some((vendor) => vendor.urn === product.urn),
  );
  const vendor = catalogProduct?.vendorProducts?.find(
    (item) => item.urn === product.urn,
  );
  const serviceType =
    catalogProduct?.serviceType?.toLowerCase() ?? (catalog ? "" : "flood");
  const schema = vendor?.metadata?.schemas?.deliverables;
  const enabledLos = (Object.keys(documentFields) as DocumentLos[]).filter(
    (los) =>
      lender.credentials?.some((credential) => credential.key === los) ||
      (los !== "encompass" && Boolean(lender.integrations?.[los])),
  );
  const dataPointFields = (
    enabledLos.length > 0
      ? (dataPointFieldsByService[serviceType] ?? [])
      : []
  )
    .filter(
      ({ key, paths }) =>
        (key !== "floodZone" ||
          enabledLos.includes("meridianlink") ||
          enabledLos.includes("encompass")) &&
        (!schema ||
          !paths ||
          paths.some((path) => hasSchemaPath(schema, path))),
    )
    .sort((left, right) => left.label.localeCompare(right.label));
  const categoryLine =
    [catalogProduct?.serviceType, catalogProduct?.productType]
      .filter(Boolean)
      .join(" · ") || "Assigned product";
  const [documents, setDocuments] = useState<
    NonNullable<UpdateProductConfigurationRequest["documents"]>
  >(() =>
    Object.fromEntries(
      enabledLos.map((los) => [
        los,
        Object.fromEntries(
          documentFields[los].map(({ key }) => [
            key,
            product.integrations?.[los]?.reportDelivery?.document?.[key] ?? "",
          ]),
        ),
      ]),
    ),
  );
  const update = useUpdateLenderProductConfiguration();
  const savedDataPoints = product.reportPolicy?.dataPoints;
  const legacyFloodPolicy =
    serviceType !== "flood" &&
    savedDataPoints?.length &&
    savedDataPoints.every((point) =>
      dataPointFieldsByService.flood.some(({ key }) => key === point),
    );
  const originalDataPoints =
    !savedDataPoints || legacyFloodPolicy
      ? dataPointFields.map(({ key }) => key)
      : savedDataPoints;
  const originalStates = product.reportPolicy?.coverage.states ?? [];
  const [dataPoints, setDataPoints] =
    useState<ReportDataPoint[]>(originalDataPoints);
  const [states, setStates] = useState<string[]>(originalStates);
  const [coverageOpen, setCoverageOpen] = useState(false);
  const isSaving = update.isPending;
  const firstInput = useRef<HTMLInputElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const changedDocuments = Object.fromEntries(
    enabledLos.flatMap((los) => {
      const changes = Object.fromEntries(
        documentFields[los]
          .filter(
            ({ key }) =>
              (documents[los]?.[key] ?? "") !==
              (product.integrations?.[los]?.reportDelivery?.document?.[key] ??
                ""),
          )
          .map(({ key }) => [key, documents[los]?.[key] ?? ""]),
      );
      return Object.keys(changes).length ? [[los, changes]] : [];
    }),
  ) as NonNullable<UpdateProductConfigurationRequest["documents"]>;
  const policyChanged =
    !sameSelection(dataPoints, originalDataPoints) ||
    !sameSelection(states, originalStates);
  const hasChanges = Object.keys(changedDocuments).length > 0 || policyChanged;

  useEffect(() => {
    (firstInput.current ?? closeButton.current)?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !isSaving) {
        if (coverageOpen) setCoverageOpen(false);
        else onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [coverageOpen, isSaving, onClose]);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!hasChanges) return;
    try {
      await update.mutateAsync({
        urn: lender.urn,
        data: {
          productUrn: product.urn,
          ...(Object.keys(changedDocuments).length > 0
            ? { documents: changedDocuments }
            : {}),
          ...(policyChanged
            ? {
                reportPolicy: {
                  dataPoints: [
                    ...new Set([
                      ...dataPoints,
                      ...originalDataPoints.filter(
                        (point) =>
                          !dataPointFields.some(({ key }) => key === point),
                      ),
                    ]),
                  ],
                  coverage: { states },
                },
              }
            : {}),
        },
      });
      onClose();
    } catch {
      // Keep the drawer open so the request error can be shown and the user can retry.
    }
  };

  return (
    <div className="fixed inset-0 z-50" role="presentation">
      <div
        className="absolute inset-x-0 bottom-0 top-[60px] bg-black/40"
        onClick={update.isPending ? undefined : onClose}
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="lender-product-edit-title"
        className="absolute bottom-0 right-0 top-[60px] w-full max-w-[668px] border border-[#ddd] bg-surface shadow-lg dark:border-border-subtle"
      >
        <button
          ref={closeButton}
          type="button"
          aria-label="Close edit product"
          onClick={onClose}
          disabled={update.isPending}
          className="absolute right-6 top-[22px] z-10"
        >
          <img src={closeIcon} alt="" />
        </button>
        <form
          onSubmit={save}
          className="flex h-full min-h-0 flex-col px-6 pb-6 pt-[22px] sm:px-10 sm:pb-14"
        >
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto">
            <h2
              id="lender-product-edit-title"
              className="text-[21px] font-medium leading-8 text-content"
            >
              Edit Product
            </h2>
            <div className="flex min-h-[62px] items-center gap-1.5 rounded-lg border border-[#ddd] bg-surface px-3 py-2 font-['Inter'] dark:border-border-subtle">
              <img src={badgeDot} alt="" />
              <div className="min-w-0 leading-none">
                <p className="truncate text-[10px] font-normal uppercase tracking-[0.5px] text-content-secondary/60">
                  {categoryLine}
                </p>
                {vendor && (
                  <p className="mt-0.5 truncate text-[11px] font-semibold uppercase tracking-[0.3px] text-content-secondary">
                    {vendor.vendorName}
                  </p>
                )}
                <p className="mt-0.5 truncate text-[14px] font-medium text-black dark:text-content">
                  {product.name}
                </p>
              </div>
            </div>

            <div className="space-y-5">
              <h3 className="text-[16px] font-medium leading-6 text-content">
                Documents
              </h3>
              {enabledLos.length === 0 && (
                <p className="text-[13px] text-content-secondary">
                  No LOS integrations are configured for this lender.
                </p>
              )}
              {enabledLos.map((los) => (
                <fieldset key={los} className="space-y-[9px]">
                  <legend
                    className={`mb-[9px] text-content-secondary ${
                      los === "meridianlink" ? "text-xs" : "text-[13px]"
                    }`}
                  >
                    {losNames[los]}
                  </legend>
                  <div className="grid grid-cols-1 gap-x-2 gap-y-[9px] pl-[15px] sm:grid-cols-2">
                    {documentFields[los].map(({ label, key }, index) => (
                      <label key={key} className="relative block pt-2.5">
                        <span className="absolute left-[10px] top-0 z-10 rounded-sm bg-surface px-1 text-xs leading-[18px] text-content-secondary">
                          {label}
                        </span>
                        <input
                          ref={
                            los === enabledLos[0] && index === 0
                              ? firstInput
                              : undefined
                          }
                          type="text"
                          maxLength={255}
                          placeholder="Input"
                          value={documents[los]?.[key] ?? ""}
                          onChange={(event) =>
                            setDocuments((current) => ({
                              ...current,
                              [los]: {
                                ...current[los],
                                [key]: event.target.value,
                              },
                            }))
                          }
                          className="h-[50px] w-full rounded border border-border bg-surface px-[15px] text-base text-content outline-none placeholder:text-content-secondary focus:border-brand focus:ring-1 focus:ring-brand"
                        />
                      </label>
                    ))}
                  </div>
                </fieldset>
              ))}
            </div>

            <section aria-label="Data Points" className="space-y-3">
              <h3 className="text-[16px] font-medium leading-6 text-content">
                Data Points
              </h3>
              <div className="grid grid-cols-1 gap-x-2 gap-y-[9px] sm:grid-cols-2">
                {dataPointFields.map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    role="switch"
                    aria-checked={dataPoints.includes(key)}
                    onClick={() =>
                      setDataPoints((current) =>
                        current.includes(key)
                          ? current.filter((item) => item !== key)
                          : [...current, key],
                      )
                    }
                    className={`flex items-center gap-3 text-left text-sm ${
                      dataPoints.includes(key)
                        ? "text-content"
                        : "text-content-secondary"
                    }`}
                  >
                    <img
                      src={dataPoints.includes(key) ? toggleOn : toggleOff}
                      alt=""
                    />
                    {label}
                  </button>
                ))}
              </div>
            </section>

            <section aria-label="Coverage" className="space-y-3">
              <h3 className="text-[16px] font-medium leading-6 text-content">
                Coverage
              </h3>
              <div className="rounded-lg border border-[#d9d9de] bg-surface px-4 py-3">
                <button
                  type="button"
                  aria-expanded={coverageOpen}
                  aria-controls="lender-product-state-coverage"
                  onClick={() => setCoverageOpen((open) => !open)}
                  className={`flex w-full items-center justify-between text-left text-[13px] text-content-secondary ${
                    coverageOpen ? "border-b border-[#d9d9de] pb-2" : ""
                  }`}
                >
                  <span>State Coverage</span>
                  <span className="ml-auto mr-4">
                    {states.length} of {stateOptions.length} states
                  </span>
                  <img
                    src={chevronDown}
                    alt=""
                    className={coverageOpen ? "rotate-180" : ""}
                  />
                </button>
                {coverageOpen && (
                  <div
                    id="lender-product-state-coverage"
                    role="group"
                    aria-label="State Coverage"
                    className="mt-3"
                  >
                    <div className="flex justify-end gap-[25px] pr-2 text-xs text-[#6265e5]">
                      <button
                        type="button"
                        onClick={() =>
                          setStates(stateOptions.map(({ code }) => code))
                        }
                      >
                        Select All
                      </button>
                      <button type="button" onClick={() => setStates([])}>
                        Clear All
                      </button>
                    </div>
                    <div className="mt-3 grid grid-flow-col grid-cols-2 grid-rows-[repeat(25,21px)] justify-between gap-y-3 pb-2.5 sm:grid-cols-5 sm:grid-rows-[repeat(10,21px)]">
                      {stateOptions.map(({ code, name }) => {
                        const selected = states.includes(code);
                        return (
                          <button
                            key={code}
                            type="button"
                            role="switch"
                            aria-label={name}
                            aria-checked={selected}
                            onClick={() =>
                              setStates((current) =>
                                current.includes(code)
                                  ? current.filter((item) => item !== code)
                                  : [...current, code],
                              )
                            }
                            className={`flex w-[67px] items-center gap-2 text-left text-sm ${
                              selected
                                ? "font-medium text-content"
                                : "text-content-secondary"
                            }`}
                          >
                            <img src={selected ? toggleOn : toggleOff} alt="" />
                            <span>{code}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </section>
            {update.isError && (
              <p role="alert" className="text-sm text-red-600">
                Unable to save product configuration. Please try again.
              </p>
            )}
          </div>
          <div className="mt-8 flex shrink-0 items-center justify-between gap-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="h-11 w-[154px] rounded-lg border border-brand bg-surface text-base font-semibold text-content disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving || !hasChanges}
              className="h-11 w-[151px] rounded-lg bg-brand text-base font-semibold text-white disabled:opacity-20"
            >
              Save Changes
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
